import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleMarker, GeoJSON, Polyline, Tooltip, useMap, ZoomControl } from 'react-leaflet';
import { ArrowsClockwise, Eye, EyeSlash, Funnel, UsersThree, X } from '@phosphor-icons/react';
import GenericMap from '../../../shared/components/common/GenericMap.jsx';
import TaskDistributionCollapsibleSection from './TaskDistributionCollapsibleSection.jsx';
import { useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';
import { getRouteColor } from '../../../styles/routeColors.js';
import {
  DEFAULT_CENTER,
  TASK_MARKER_RING_LEGEND,
  buildZoneSimulationData
} from '../utils/taskDistributionUtils.jsx';

function buildBoundsSignature(bounds = []) {
  return bounds
    .map((point) => (
      Array.isArray(point)
        ? point.map((value) => Number(value).toFixed(6)).join(',')
        : String(point)
    ))
    .join('|');
}

function MapViewport({ bounds }) {
  const map = useMap();
  const boundsSignature = buildBoundsSignature(bounds);

  useEffect(() => {
    if (!bounds.length || !boundsSignature) {
      return;
    }

    map.fitBounds(bounds, {
      padding: [36, 36]
    });
  }, [boundsSignature, map]);

  return null;
}

function MarkerLegendSwatch({ ringBackground }) {
  return (
    <span
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full p-[2px]"
      style={{ background: ringBackground }}
    >
      <span className="h-full w-full rounded-full border border-white/90 bg-content-muted" />
    </span>
  );
}

const SIMULATION_STEP_DURATIONS = {
  intro: 900,
  angle: 180,
  seed: 520,
  assign: 420,
  complete: 1100
};

function buildTeamColorMap(teams = []) {
  return teams.reduce((result, team) => {
    result[team.teamId] = team.color;
    return result;
  }, {});
}

function buildTeamNameMap(teams = []) {
  return teams.reduce((result, team) => {
    result[team.teamId] = team.teamName;
    return result;
  }, {});
}

function interpolatePosition(startPosition, endPosition, progress) {
  const [startLat, startLng] = startPosition;
  const [endLat, endLng] = endPosition;

  return [
    startLat + ((endLat - startLat) * progress),
    startLng + ((endLng - startLng) * progress)
  ];
}

function buildPointCentroid(points = [], fallbackPoint) {
  if (!points.length) {
    return fallbackPoint;
  }

  const totals = points.reduce((accumulator, point) => ({
    lat: accumulator.lat + point.lat,
    lng: accumulator.lng + point.lng
  }), { lat: 0, lng: 0 });

  return {
    lat: totals.lat / points.length,
    lng: totals.lng / points.length
  };
}

function buildSimulationView(simulationData, stepIndex, stepProgress) {
  if (!simulationData) {
    return null;
  }

  const safeStepIndex = Math.max(0, Math.min(stepIndex, simulationData.steps.length - 1));
  const currentStep = simulationData.steps[safeStepIndex] || simulationData.steps[0];
  const currentProgress = Math.max(0, Math.min(1, stepProgress));
  const teamColorById = buildTeamColorMap(simulationData.teams);
  const teamNameById = buildTeamNameMap(simulationData.teams);
  const taskById = new Map(simulationData.tasks.map((task) => [task.id, task]));
  const assignedTaskIds = new Set();
  const seedTaskIds = new Set();
  const angleTaskIds = new Set();
  const persistedLines = [];
  const teamTaskIds = new Map(simulationData.teams.map((team) => [team.teamId, []]));

  const applyCompletedStep = (step) => {
    if (!step) {
      return;
    }

    if (step.kind === 'angle' && step.taskId) {
      angleTaskIds.add(step.taskId);
      persistedLines.push({
        id: `line-angle-${step.taskId}`,
        positions: [step.startPosition, step.endPosition],
        color: 'var(--app-text-muted)',
        opacity: 0.32,
        weight: 2,
        dashArray: '6 10'
      });
      return;
    }

    if ((step.kind === 'seed' || step.kind === 'assign') && step.taskId && step.teamId) {
      const teamColor = teamColorById[step.teamId] || getRouteColor();
      assignedTaskIds.add(step.taskId);

      if (step.kind === 'seed') {
        seedTaskIds.add(step.taskId);
      }

      const currentTeamTaskIds = teamTaskIds.get(step.teamId) || [];
      currentTeamTaskIds.push(step.taskId);
      teamTaskIds.set(step.teamId, currentTeamTaskIds);

      persistedLines.push({
        id: `line-${step.kind}-${step.taskId}`,
        positions: [step.startPosition, step.endPosition],
        color: teamColor,
        opacity: step.kind === 'seed' ? 0.8 : 0.56,
        weight: step.kind === 'seed' ? 3 : 3,
        dashArray: step.kind === 'seed' ? '10 10' : undefined
      });
    }
  };

  for (let index = 0; index < safeStepIndex; index += 1) {
    applyCompletedStep(simulationData.steps[index]);
  }

  const activeLines = [];
  const activeTaskId = currentStep?.taskId || null;
  const activeTeamId = currentStep?.teamId || null;

  if (currentStep?.kind === 'angle' && currentStep.endPosition && currentStep.startPosition) {
    const partialEndPosition = interpolatePosition(currentStep.startPosition, currentStep.endPosition, currentProgress);
    angleTaskIds.add(currentStep.taskId);
    activeLines.push({
      id: `active-angle-${currentStep.taskId}`,
      positions: [currentStep.startPosition, partialEndPosition],
      color: 'var(--app-text)',
      opacity: 0.9,
      weight: 2.5,
      dashArray: '6 8'
    });
  }

  if ((currentStep?.kind === 'seed' || currentStep?.kind === 'assign') && currentStep.endPosition && currentStep.startPosition) {
    const teamColor = teamColorById[currentStep.teamId] || getRouteColor();
    const partialEndPosition = interpolatePosition(currentStep.startPosition, currentStep.endPosition, currentProgress);
    activeLines.push({
      id: `active-${currentStep.kind}-${currentStep.taskId}`,
      positions: [currentStep.startPosition, partialEndPosition],
      color: teamColor,
      opacity: 0.98,
      weight: 3.5,
      dashArray: currentStep.kind === 'seed' ? '10 10' : undefined
    });
  }

  const teamCenters = simulationData.teams
    .map((team) => {
      const currentTeamTaskIds = teamTaskIds.get(team.teamId) || [];
      if (!currentTeamTaskIds.length) {
        return null;
      }

      const points = currentTeamTaskIds
        .map((taskId) => taskById.get(taskId))
        .filter(Boolean)
        .map((task) => ({ lat: task.lat, lng: task.lng }));
      const centerPoint = buildPointCentroid(points, {
        lat: simulationData.basePosition[0],
        lng: simulationData.basePosition[1]
      });

      return {
        teamId: team.teamId,
        teamName: team.teamName,
        color: team.color,
        position: [centerPoint.lat, centerPoint.lng]
      };
    })
    .filter(Boolean);

  const taskMarkers = simulationData.tasks.map((task) => {
    const taskAssigned = assignedTaskIds.has(task.id);
    const taskIsActive = task.id === activeTaskId;
    const taskIsSeed = seedTaskIds.has(task.id) || (currentStep?.kind === 'seed' && currentStep.taskId === task.id);
    const taskTeamId = taskAssigned
      ? task.finalTeamId
      : (taskIsSeed ? activeTeamId || task.finalTeamId : task.finalTeamId);
    const teamColor = taskTeamId ? (teamColorById[taskTeamId] || getRouteColor()) : 'var(--app-text)';
    const isUnassigned = simulationData.unassignedTaskIds.includes(task.id);
    const markerStroke = task.isPreassigned
      ? 'var(--st-pendiente)'
      : (taskIsActive ? '#ffffff' : 'rgb(var(--app-bg-rgb) / 0.92)');

    let fillColor = 'var(--app-text-muted)';
    let fillOpacity = 0.92;
    let radius = 7;

    if (taskAssigned || taskIsSeed) {
      fillColor = teamColor;
      fillOpacity = 0.96;
      radius = 7.5;
    }

    if (safeStepIndex === simulationData.steps.length - 1 && currentStep?.kind === 'complete' && isUnassigned) {
      fillColor = 'var(--warning)';
      fillOpacity = 0.96;
    }

    if (taskIsActive) {
      fillColor = currentStep?.kind === 'angle' ? '#ffffff' : teamColor;
      fillOpacity = 1;
      radius = 9;
    }

    return {
      ...task,
      fillColor,
      fillOpacity,
      radius,
      strokeColor: markerStroke,
      strokeWeight: taskIsActive ? 3 : 2.5
    };
  });

  const currentStageLabel = currentStep?.kind === 'angle'
    ? 'Lectura angular'
    : currentStep?.kind === 'seed'
      ? 'Semillas de zona'
      : currentStep?.kind === 'assign'
        ? 'Asignacion progresiva'
        : currentStep?.kind === 'complete'
          ? 'Rutas finales'
          : 'Inicio';

  return {
    activeTaskId,
    activeTeamId,
    currentStep,
    currentStageLabel,
    progressLabel: `${safeStepIndex + 1}/${simulationData.steps.length}`,
    taskMarkers,
    teamCenters,
    lines: [...persistedLines, ...activeLines],
    showFinalRoutes: currentStep?.kind === 'complete',
    bounds: simulationData.bounds,
    basePosition: simulationData.basePosition,
    currentTeamName: activeTeamId ? (teamNameById[activeTeamId] || 'Equipo') : ''
  };
}

function SimulationStatusCard({
  simulationView,
  simulationStepIndex,
  totalSteps,
  isSimulationPlaying,
  onReplay,
  onClose
}) {
  if (!simulationView) {
    return null;
  }

  const progressPercent = totalSteps > 1
    ? Math.round((simulationStepIndex / (totalSteps - 1)) * 100)
    : 100;

  return (
    <div className="absolute bottom-4 left-4 z-[1000] w-[min(340px,calc(100%-2rem))] rounded-3xl border border-line bg-surface-2/95 px-4 py-4 text-content shadow-[0_22px_44px_rgba(0,0,0,0.45)] backdrop-blur">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-content-muted">
            Simulacion Zonal
          </div>
          <div className="mt-1 text-lg font-semibold text-content">{simulationView.currentStageLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReplay}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-surface text-content transition hover:bg-surface-2"
            title="Repetir simulación"
            aria-label="Repetir simulación"
          >
            <ArrowsClockwise size={18} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-surface text-content transition hover:bg-surface-2"
            title="Cerrar simulación"
            aria-label="Cerrar simulación"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 text-xs text-content-muted">
        <span>{simulationView.progressLabel}</span>
        <span>{isSimulationPlaying ? 'Reproduciendo' : 'Vista final'}</span>
      </div>

      <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-2">
        <div
          className="h-full rounded-full bg-amber transition-[width] duration-200"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="mt-3 text-sm leading-6 text-content">
        {simulationView.currentStep?.description}
      </div>

      {simulationView.currentTeamName && (
        <div className="mt-3 inline-flex items-center rounded-full border border-amber/30 bg-[--app-sidebar-active] px-3 py-1 text-xs font-medium text-accent-text">
          Equipo activo: {simulationView.currentTeamName}
        </div>
      )}
    </div>
  );
}

function RouteVisibilityPanel({
  distributionResult,
  resultMode = 'preview',
  routeAssignments,
  visibleRouteTeamIdSet,
  onToggleRouteVisibility,
  onShowAllRoutes,
  onHideAllRoutes,
  className = ''
}) {
  return (
    <div className={`flex h-full flex-col bg-surface ${className}`}>
      <div className="border-b border-line px-6 py-4">
        <h3 className="text-2xl font-bold text-content">Filtros</h3>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div>
          <div className="text-sm font-semibold text-content">Recorridos por equipo</div>
          <p className="mt-1 text-xs text-content-muted">
            Oculta una o varias lineas para comparar rutas sin perder los puntos de tarea.
          </p>
        </div>
        <div className="mt-4 space-y-2">
          {routeAssignments.length > 0 && routeAssignments.map((assignment, index) => {
            const normalizedTeamId = String(assignment.teamId);
            const isVisible = visibleRouteTeamIdSet.has(normalizedTeamId);
            const routeColor = assignment.color || getRouteColor(index);

            return (
              <button
                key={`route-toggle-${assignment.teamId}`}
                type="button"
                onClick={() => onToggleRouteVisibility(assignment.teamId)}
                className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-3 py-3 text-left transition ${
                  isVisible
                    ? 'border-amber/40 bg-[--app-sidebar-active]'
                    : 'border-line bg-surface opacity-80'
                }`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="relative h-3 w-10 shrink-0">
                    <span
                      className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full"
                      style={{ backgroundColor: routeColor }}
                    />
                  </span>
                  <span className="truncate text-sm font-medium text-content">{assignment.teamName}</span>
                </div>

                <span className={`inline-flex items-center text-xs font-medium ${isVisible ? 'text-accent-text' : 'text-content-muted'}`}>
                </span>
              </button>
            );
          })}

          {!distributionResult && (
            <div className="rounded-2xl border border-dashed border-line bg-surface-2 px-3 py-3 text-sm text-content-muted">
              Realiza una ejecución para poder mostrar u ocultar recorridos por equipo.
            </div>
          )}

          {distributionResult && routeAssignments.length === 0 && (
            <div className="rounded-2xl border border-dashed border-line bg-surface-2 px-3 py-3 text-sm text-content-muted">
              {resultMode === 'confirmed'
                ? 'No hay recorridos confirmados para mostrar.'
                : 'La ejecución no devolvió recorridos.'}
            </div>
          )}
        </div>
      </div>

      {routeAssignments.length > 0 && (
        <div className="space-y-2 border-t border-line px-4 py-4">
          <button
            type="button"
            onClick={onHideAllRoutes}
            className="w-full rounded-xl bg-surface-2 px-3 py-2 text-sm font-semibold text-content transition hover:bg-line"
          >
            Ocultar todas
          </button>
          <button
            type="button"
            onClick={onShowAllRoutes}
            className="w-full rounded-xl bg-amber px-3 py-2 text-sm font-semibold text-on-amber transition hover:bg-amber-hover"
          >
            Mostrar todas
          </button>
        </div>
      )}
    </div>
  );
}

const TaskDistributionMapSection = ({
  distributionResult,
  simulationDistributionResult = null,
  resultMode = 'preview',
  mapArtifacts,
  isCalculating,
  routeAssignments,
  selectedTasks,
  showConfirmedOverlayNotice = false,
  showRefreshConfirmedRoutes = false,
  visibleRouteTeamIds,
  onRefreshConfirmedRoutes,
  onToggleRouteVisibility,
  onShowAllRoutes,
  onHideAllRoutes
}) => {
  const [showRoutePanel, setShowRoutePanel] = useState(false);
  // Recorrido guiado: el paso con `panel` muestra «Recorridos por equipo» sin animación (driver.js
  // mide el panel al mostrarse); al salir del paso vuelve a lo que el usuario tenía.
  const [isTourRoutePanel, setIsTourRoutePanel] = useState(false);
  useGuidedTourStep(useCallback((tourStep) => setIsTourRoutePanel(Boolean(tourStep?.panel)), []));
  const isRoutePanelOpen = showRoutePanel || isTourRoutePanel;
  const [showSimulation, setShowSimulation] = useState(false);
  const [simulationStepIndex, setSimulationStepIndex] = useState(0);
  const [simulationStepProgress, setSimulationStepProgress] = useState(0);
  const [isSimulationPlaying, setIsSimulationPlaying] = useState(false);
  const simulationSourceResult = simulationDistributionResult || distributionResult;
  const simulationData = useMemo(() => {
    return buildZoneSimulationData({
      tasks: selectedTasks,
      distributionResult: simulationSourceResult
    });
  }, [simulationSourceResult, selectedTasks]);
  const visibleRouteTeamIdSet = new Set((visibleRouteTeamIds || []).map((teamId) => String(teamId)));
  const routeAssignmentTeamIdSet = new Set(routeAssignments.map((assignment) => String(assignment.teamId)));
  const visibleRoutes = mapArtifacts.geoJsonRoutes.filter((route) => visibleRouteTeamIdSet.has(String(route.teamId)));
  const visibleMarkers = mapArtifacts.markers.filter((marker) => {
    const normalizedTeamId = String(marker.teamId);

    if (marker.isBase) {
      return true;
    }

    if (!routeAssignmentTeamIdSet.has(normalizedTeamId)) {
      return true;
    }

    return visibleRouteTeamIdSet.has(normalizedTeamId);
  });

  useEffect(() => {
    if (!distributionResult) {
      setShowRoutePanel(false);
      setShowSimulation(false);
      setSimulationStepIndex(0);
      setSimulationStepProgress(0);
      setIsSimulationPlaying(false);
    }
  }, [distributionResult]);

  useEffect(() => {
    if (!showSimulation || !isSimulationPlaying || !simulationData?.steps?.length) {
      return undefined;
    }

    const currentStep = simulationData.steps[simulationStepIndex];
    if (!currentStep) {
      setIsSimulationPlaying(false);
      return undefined;
    }

    const durationMs = SIMULATION_STEP_DURATIONS[currentStep.kind] || 420;
    let animationFrameId = null;
    let startTimeMs = null;

    const tick = (timestampMs) => {
      if (startTimeMs == null) {
        startTimeMs = timestampMs;
      }

      const elapsedMs = timestampMs - startTimeMs;
      const progress = Math.max(0, Math.min(1, elapsedMs / durationMs));
      setSimulationStepProgress(progress);

      if (progress >= 1) {
        const nextStepIndex = simulationStepIndex + 1;
        if (nextStepIndex < simulationData.steps.length) {
          setSimulationStepIndex(nextStepIndex);
          setSimulationStepProgress(0);
        } else {
          setSimulationStepProgress(1);
          setIsSimulationPlaying(false);
        }
        return;
      }

      animationFrameId = window.requestAnimationFrame(tick);
    };

    animationFrameId = window.requestAnimationFrame(tick);

    return () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [showSimulation, isSimulationPlaying, simulationData, simulationStepIndex]);

  useEffect(() => {
    if (showSimulation && !simulationData) {
      setShowSimulation(false);
      setSimulationStepIndex(0);
      setSimulationStepProgress(0);
      setIsSimulationPlaying(false);
    }
  }, [showSimulation, simulationData]);

  const simulationView = showSimulation
    ? buildSimulationView(simulationData, simulationStepIndex, simulationStepProgress)
    : null;
  const canShowSimulation = Boolean(simulationData?.steps?.length) && !isCalculating;
  const activeBounds = simulationView?.bounds?.length ? simulationView.bounds : mapArtifacts.bounds;
  const renderedMarkers = showSimulation ? [] : visibleMarkers;
  const renderedRoutes = showSimulation
    ? (simulationView?.showFinalRoutes ? visibleRoutes : [])
    : visibleRoutes;

  const handleStartSimulation = () => {
    if (!canShowSimulation) {
      return;
    }

    setShowRoutePanel(false);
    setShowSimulation(true);
    setSimulationStepIndex(0);
    setSimulationStepProgress(0);
    setIsSimulationPlaying(true);
  };

  const handleCloseSimulation = () => {
    setShowSimulation(false);
    setSimulationStepIndex(0);
    setSimulationStepProgress(0);
    setIsSimulationPlaying(false);
  };

  const handleReplaySimulation = () => {
    if (!simulationData?.steps?.length) {
      return;
    }

    setShowSimulation(true);
    setSimulationStepIndex(0);
    setSimulationStepProgress(0);
    setIsSimulationPlaying(true);
  };

  return (
    <TaskDistributionCollapsibleSection
      title="Mapa de rutas"
      description="Visualiza los recorridos de las tareas distribuidas en el mapa."
      dataTour="dist-map"
      icon={<UsersThree size={20} />}
    >
      {resultMode === 'confirmed' && distributionResult && (
        <div className="mb-4 rounded-2xl border border-amber/30 bg-[--app-sidebar-active] px-4 py-3 text-sm text-accent-text">
          Mostrando los recorridos confirmados del día. Realiza una nueva ejecución si quieres generar otra previsualización.
        </div>
      )}

      {showConfirmedOverlayNotice && (
        <div className="mb-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          La previsualizacion actual se muestra junto con los recorridos ya confirmados del mismo dia para no perder referencia.
        </div>
      )}

      <div className="space-y-4">
        <div className="relative h-[420px] overflow-hidden rounded-[18px] border border-line md:h-[520px] xl:h-[560px]">
          <button
            type="button"
            onClick={showSimulation ? handleCloseSimulation : handleStartSimulation}
            disabled={!canShowSimulation && !showSimulation}
            className={`absolute left-4 top-4 z-[1000] rounded-full border-[3px] border-white/90 p-3 text-white shadow-[0_14px_30px_rgba(15,23,42,0.45)] transition ${
              showSimulation
                ? 'bg-amber hover:bg-amber-hover'
                : (!canShowSimulation && !showSimulation)
                  ? 'cursor-not-allowed bg-surface-2 opacity-60'
                  : 'bg-surface hover:scale-[1.03] hover:bg-surface-2'
            }`}
            title={showSimulation ? 'Ocultar simulación' : 'Ver simulación de cálculo'}
            aria-label={showSimulation ? 'Ocultar simulación' : 'Ver simulación de cálculo'}
          >
            {showSimulation ? <EyeSlash size={20} weight="fill" /> : <Eye size={20} weight="fill" />}
          </button>

          <button
            type="button"
            onClick={() => setShowRoutePanel((current) => !current)}
            aria-expanded={isRoutePanelOpen}
            disabled={showSimulation}
            className={`absolute right-4 top-4 z-[1000] rounded-full border-[3px] border-white/90 p-3 text-white shadow-[0_14px_30px_rgba(15,23,42,0.45)] transition ${
              showSimulation
                ? 'cursor-not-allowed bg-surface-2 opacity-50'
                : 'bg-surface hover:scale-[1.03] hover:bg-surface-2'
            }`}
            title="Filtros"
            aria-label="Abrir panel de filtros"
          >
            <Funnel size={20} weight="fill" />
          </button>

          <div
            data-tour="dist-route-panel"
            className={`absolute right-0 top-0 z-[999] h-full w-72 max-w-[88%] border-l border-line shadow-2xl ${
              isTourRoutePanel ? '' : 'transition-transform duration-300 ease-in-out'
            } ${
              isRoutePanelOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <RouteVisibilityPanel
              distributionResult={distributionResult}
              resultMode={resultMode}
              routeAssignments={routeAssignments}
              visibleRouteTeamIdSet={visibleRouteTeamIdSet}
              onToggleRouteVisibility={onToggleRouteVisibility}
              onShowAllRoutes={onShowAllRoutes}
              onHideAllRoutes={onHideAllRoutes}
            />
          </div>

          <GenericMap
            center={DEFAULT_CENTER}
            zoom={12}
            zoomControl={false}
            style={{ height: '100%', width: '100%' }}
            className="h-full w-full"
            markers={renderedMarkers}
            loading={isCalculating}
          >
            <ZoomControl position="bottomright" />

            {renderedRoutes.map((route) => (
              <GeoJSON
                key={`${route.id}-${JSON.stringify(route.geometry)}`}
                data={route.geometry}
                style={() => ({
                  color: route.color,
                  weight: 5,
                  opacity: 0.95
                })}
              />
            ))}

            {simulationView && (
              <>
                {simulationView.lines.map((line) => (
                  <Polyline
                    key={line.id}
                    positions={line.positions}
                    pathOptions={{
                      color: line.color,
                      weight: line.weight,
                      opacity: line.opacity,
                      dashArray: line.dashArray
                    }}
                  />
                ))}

                <CircleMarker
                  center={simulationView.basePosition}
                  radius={10}
                  pathOptions={{
                    color: 'var(--app-bg)',
                    weight: 3,
                    fillColor: '#ffffff',
                    fillOpacity: 1
                  }}
                >
                  <Tooltip direction="top" offset={[0, -8]}>
                    Base operativa
                  </Tooltip>
                </CircleMarker>

                {simulationView.teamCenters.map((center) => (
                  <CircleMarker
                    key={`center-${center.teamId}`}
                    center={center.position}
                    radius={6}
                    pathOptions={{
                      color: '#ffffff',
                      weight: 2,
                      fillColor: center.color,
                      fillOpacity: 0.9
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -6]}>
                      Centro actual de {center.teamName}
                    </Tooltip>
                  </CircleMarker>
                ))}

                {simulationView.taskMarkers.map((task) => (
                  <CircleMarker
                    key={`simulation-task-${task.id}`}
                    center={[task.lat, task.lng]}
                    radius={task.radius}
                    pathOptions={{
                      color: task.strokeColor,
                      weight: task.strokeWeight,
                      fillColor: task.fillColor,
                      fillOpacity: task.fillOpacity
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -8]}>
                      {task.title}
                    </Tooltip>
                  </CircleMarker>
                ))}
              </>
            )}

            <MapViewport bounds={activeBounds} />
          </GenericMap>

          {showRefreshConfirmedRoutes && (
            <div className="absolute inset-0 z-[998] flex items-center justify-center bg-bg/40 backdrop-blur-[3px]">
              <button
                type="button"
                onClick={onRefreshConfirmedRoutes}
                disabled={isCalculating}
                className="inline-flex items-center gap-3 rounded-full border border-amber/40 bg-amber px-7 py-4 text-base font-semibold text-on-amber shadow-[0_20px_50px_rgba(245,183,49,0.32)] transition hover:scale-[1.02] hover:bg-amber-hover disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isCalculating ? <ArrowsClockwise size={20} className="animate-spin" /> : <ArrowsClockwise size={20} />}
                {isCalculating ? 'Actualizando...' : 'Actualizar'}
              </button>
            </div>
          )}

          {simulationView && (
            <SimulationStatusCard
              simulationView={simulationView}
              simulationStepIndex={simulationStepIndex}
              totalSteps={simulationData?.steps?.length || 1}
              isSimulationPlaying={isSimulationPlaying}
              onReplay={handleReplaySimulation}
              onClose={handleCloseSimulation}
            />
          )}
        </div>

        <div className="px-1 pt-1">
          <div className="mb-4 text-[15px] font-semibold text-content">Leyenda de Estados</div>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3">
            {TASK_MARKER_RING_LEGEND.map((item) => (
              <div key={item.key} className="flex items-center gap-3">
                <MarkerLegendSwatch ringBackground={item.ringBackground} />
                <span className="text-sm font-medium text-content">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {(mapArtifacts.overlappingGroups.length > 0 || mapArtifacts.invalidStops.length > 0) && (
        <div className="mt-4 space-y-3">
          {mapArtifacts.overlappingGroups.length > 0 && (
            <div className="rounded-2xl border border-amber/30 bg-[--app-sidebar-active] px-4 py-3 text-sm text-accent-text">
              Se separaron visualmente {mapArtifacts.overlappingGroups.length} grupos de paradas que comparten coordenadas para que no se tapen entre si.
            </div>
          )}

          {mapArtifacts.invalidStops.length > 0 && (
            <div className="rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
              <div className="font-semibold">Paradas sin marcador por coordenadas invalidas</div>
              <div className="mt-2 space-y-1 text-xs text-warning">
                {mapArtifacts.invalidStops.slice(0, 5).map((stop, index) => (
                  <div key={`${stop.teamName}-${stop.order}-${index}`}>
                    {stop.teamName} - #{stop.order} - {stop.title}
                  </div>
                ))}
                {mapArtifacts.invalidStops.length > 5 && (
                  <div>y {mapArtifacts.invalidStops.length - 5} mas...</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!distributionResult && (
        <div className="mt-4 rounded-2xl border border-dashed border-line bg-surface-2 px-4 py-4 text-sm text-content-muted">
          La previsualización aparecera aca cuando ejecutes el cálculo.
        </div>
      )}

      {distributionResult && !canShowSimulation && (
        <div className="mt-4 rounded-2xl border border-dashed border-line bg-surface-2 px-4 py-4 text-sm text-content-muted">
          La simulación visual necesita una ejecución con tareas visibles y coordenadas válidas.
        </div>
      )}
    </TaskDistributionCollapsibleSection>
  );
};

export default TaskDistributionMapSection;
